import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface PopularCategoriesProps {
  currentLang: string;
}

export default function PopularCategories({ currentLang }: PopularCategoriesProps) {
  const { t } = useTranslation('home');
  const navigate = useNavigate();

  const categoryCards = [
    {
      key: 'categories_clothing',
      descKey: 'categories_clothing_desc',
      slug: 'clothing',
      icon: 'ri-shirt-line',
      image:
        'https://readdy.ai/api/search-image?query=Bright%20minimalist%20boutique%20interior%20with%20organized%20clothing%20rack%20white%20walls%20and%20green%20plants%20wide%20editorial%20shot%20soft%20daylight%20clean%20airy%20retail%20aesthetic%20high%20detail%20fashion%20merchandising&width=400&height=300&seq=cat-clothing-001&orientation=landscape&nocache=true',
    },
    {
      key: 'categories_shoes',
      descKey: 'categories_shoes_desc',
      slug: 'shoes',
      icon: 'ri-footprint-line',
      image:
        'https://readdy.ai/api/search-image?query=Single%20luxury%20white%20leather%20sneaker%20on%20a%20minimal%20stone%20pedestal%20with%20soft%20grey%20studio%20backdrop%20high%20end%20product%20photography%20elegant%20shadows%20premium%20minimalist%20aesthetic%20high%20detail&width=400&height=300&seq=cat-shoes-001&orientation=landscape&nocache=true',
    },
    {
      key: 'categories_fashion',
      descKey: 'categories_fashion_desc',
      slug: 'fashion',
      icon: 'ri-t-shirt-2-line',
      image:
        'https://readdy.ai/api/search-image?query=Creative%20fashion%20moodboard%20with%20magazine%20cutouts%20fabric%20swatches%20and%20color%20palette%20cards%20arranged%20on%20a%20designer%20desk%20artistic%20studio%20flatlay%20vibrant%20editorial%20composition%20high%20detail&width=400&height=300&seq=cat-fashion-001&orientation=landscape&nocache=true',
    },
    {
      key: 'categories_electronics',
      descKey: 'categories_electronics_desc',
      slug: 'electronics',
      icon: 'ri-smartphone-line',
      image:
        'https://readdy.ai/api/search-image?query=Smartphone%20wireless%20earbuds%20and%20smartwatch%20on%20a%20clean%20white%20desk%20with%20geometric%20light%20and%20shadow%20patterns%20modern%20tech%20product%20photography%20minimal%20premium%20aesthetic%20high%20detail&width=400&height=300&seq=cat-electronics-001&orientation=landscape&nocache=true',
    },
    {
      key: 'categories_accessories',
      descKey: 'categories_accessories_desc',
      slug: 'accessories',
      icon: 'ri-handbag-line',
      image:
        'https://readdy.ai/api/search-image?query=Luxury%20wristwatch%20leather%20wallet%20and%20sunglasses%20inside%20an%20elegant%20gift%20box%20with%20silk%20lining%20premium%20product%20photography%20warm%20sophisticated%20lighting%20high%20detail%20reflective%20surfaces&width=400&height=300&seq=cat-accessories-001&orientation=landscape&nocache=true',
    },
    {
      key: 'categories_sellers',
      descKey: 'categories_sellers_desc',
      slug: 'trusted-sellers',
      icon: 'ri-shield-check-line',
      image:
        'https://readdy.ai/api/search-image?query=Golden%20shield%20badge%20with%20a%20checkmark%20floating%20on%20a%20deep%20green%20gradient%20background%20trust%20and%20security%20concept%20professional%203D%20render%20clean%20minimal%20composition%20high%20detail&width=400&height=300&seq=cat-sellers-001&orientation=landscape&nocache=true',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-16 max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <span className="text-accent-500 text-xs font-semibold uppercase tracking-[0.15em] mb-3 block">
            Categories
          </span>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground-900">
            {t('categories_h2')}
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {categoryCards.map((card) => (
            <div
              key={card.key}
              onClick={() => navigate(`/${currentLang}/allchinabuy-spreadsheet/${card.slug}/`)}
              className="card-hover group bg-background-50 rounded-xl overflow-hidden border border-background-200/60 cursor-pointer"
            >
              <div className="relative h-44 md:h-48 overflow-hidden">
                <img
                  src={card.image}
                  alt={t(card.key)}
                  title={`${t(card.key)} - AllChinaBuy Spreadsheet`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-900/40 to-foreground-900/5" />
                <div className="absolute bottom-3 left-4 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-background-50/20 backdrop-blur-sm flex items-center justify-center">
                    <i className={`${card.icon} text-background-50 text-sm`}></i>
                  </div>
                </div>
              </div>
              <div className="p-5 md:p-6">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors">
                  {t(card.key)}
                </h3>
                <p className="text-foreground-500 text-sm leading-relaxed mb-4">
                  {t(card.descKey)}
                </p>
                <span className="inline-flex items-center gap-1 text-primary-600 text-sm font-medium group-hover:text-primary-700 transition-colors">
                  Explore {t(card.key)}
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}